/**
 * Auth Callback Page
 *
 * Handles the OAuth callback redirect from the server.
 * Checks if the user is authenticated, merges device cards, then redirects.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { mergeDeviceToGoogleUser, buildAuthHeaders } from '@/lib/auth-client'
import { consumeReturnPath } from '@/lib/auth-redirect'
import { useT } from '@/contexts/I18nContext'
import { API_URL } from '@/env'

/** Same-origin by default (single-domain mode); API_URL when split-domain. */
const BASE_URL = API_URL || (typeof window !== 'undefined' ? window.location.origin : '')

/**
 * First-touch attribution (extension-activation-quickpath.md P0-4): map the
 * stashed OAuth return path to a whitelisted users.source value. Server keeps
 * the first touch (never overwrites); failure here must never block login.
 */
async function reportSignupSource(returnPath: string | null): Promise<void> {
  const source = returnPath?.includes('via=extension')
    ? 'extension'
    : returnPath?.includes('source=extension_welcome')
      ? 'extension_welcome'
      : null
  if (!source) return
  try {
    await fetch(`${BASE_URL}/api/auth/source`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
      body: JSON.stringify({ source }),
      credentials: 'include',
    })
  } catch {
    // fire-and-forget — attribution loss is acceptable, login failure is not
  }
}

export function AuthCallback() {
  const { refreshUser } = useAuth()
  const t = useT()
  const navigate = useNavigate()
  const [error, setError] = useState(false)

  useEffect(() => {
    async function handleCallback() {
      try {
        await refreshUser()
        // Merge device-based cards into Google account BEFORE navigating,
        // so the home page loads with the correct card data.
        await mergeDeviceToGoogleUser()
        // Return to a stashed deep link if RequireAuth saved one (e.g. a paid
        // checkout that landed with an expired session); else the app home.
        const returnPath = consumeReturnPath()
        void reportSignupSource(returnPath)
        navigate(returnPath ?? '/app', { replace: true })
      } catch {
        setError(true)
      }
    }
    handleCallback()
  }, [refreshUser, navigate])

  // Show error after timeout
  useEffect(() => {
    const timer = setTimeout(() => {
      setError(true)
    }, 10000)
    return () => clearTimeout(timer)
  }, [])

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-surface px-6">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-error mx-auto mb-4" />
          <h2 className="font-headline font-bold text-xl text-on-surface mb-2">
            {t('auth.signInFailed')}
          </h2>
          <p className="text-on-surface-variant mb-6">
            {t('auth.signInError')}
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-3 rounded-full bg-primary text-on-primary font-bold"
          >
            {t('auth.tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[100dvh] bg-surface">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-on-surface-variant font-medium">{t('auth.signingYouIn')}</p>
      </div>
    </div>
  )
}
